/**
 * « Relire mes journaux » (web-pwa §5.6).
 *
 * Demandé par Hedi le 2 septembre 2026 : « ce journal devrait être consultable
 * par l'élève plus tard ». Un enfant à qui l'on demande d'écrire tous les soirs
 * quelque chose qu'il ne pourra jamais relire n'écrit pas un journal, il remplit
 * un formulaire. Et c'est ce qu'il vient chercher : « qu'est-ce que j'avais dit
 * sur le contrôle de la semaine dernière ».
 *
 * Trente jours au plus (`journal.HISTORIQUE_PAR_DEFAUT`), du plus récent au plus
 * ancien. Un jour sans entrée n'y figure pas ; un jour répondu « rien à dire »
 * y figure et le dit — la différence appartient à l'enfant autant qu'aux
 * parents.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { call } from "@/api/client";
import type { JournalDuJour } from "@/api/schema";
import { RelectureDuJournal } from "@/child/JournalScreen";
import { enClairCourt } from "@/ui/dates";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "liste"; journaux: JournalDuJour[] };

export function JournalHistoriqueScreen() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    setEtat({ quoi: "chargement" });
    try {
      const recu = await call("historiqueDeMonJournal");
      setEtat({ quoi: "liste", journaux: recu.journaux ?? [] });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (etat.quoi === "chargement") return <Loading label="Je relis…" />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Mes journaux">
        <ErrorNote>Je n'arrive pas à relire tes journaux.</ErrorNote>
        <Button onClick={() => void charger()}>Réessayer</Button>
      </Screen>
    );
  }

  return (
    <Screen title="Mes journaux" lead="Ce que tu as écrit ces dernières semaines.">
      {etat.journaux.length === 0 ? (
        <Empty>Tu n'as encore rien écrit. Ça commence ce soir.</Empty>
      ) : (
        etat.journaux.map((journal) => (
          <section key={journal.le} className="journal__jour">
            <h2 className="journal__date">{enClairCourt(journal.le)}</h2>
            {journal.nothing_to_say && (journal.lignes ?? []).length === 0 ? (
              <Card>
                <p className="muted">Rien de spécial ce jour-là.</p>
              </Card>
            ) : (
              <RelectureDuJournal lignes={journal.lignes ?? []} />
            )}
          </section>
        ))
      )}
      <p className="aside">
        <Link to="/eleve">← Revenir au journal d'aujourd'hui</Link>
      </p>
    </Screen>
  );
}
